import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { cn } from '../../lib/utils';

const DropdownMenuContext = createContext({});

function DropdownMenu({ children }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerContainerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const isOutsideTrigger =
        triggerContainerRef.current && !triggerContainerRef.current.contains(event.target);
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      const isMenuNotMounted = !menuRef.current;

      if (isOutsideTrigger && (isOutsideMenu || isMenuNotMounted)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const contextValue = useMemo(
    () => ({ open, setOpen, position, setPosition, menuRef }),
    [open, position],
  );

  return (
    <DropdownMenuContext.Provider value={contextValue}>
      <div ref={triggerContainerRef} className="relative inline-block">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

DropdownMenu.propTypes = {
  children: PropTypes.node.isRequired,
};

function DropdownMenuTrigger({ children, asChild }) {
  const { open, setOpen, setPosition } = useContext(DropdownMenuContext);
  const triggerRef = useRef(null);

  const handleClick = (e) => {
    e.stopPropagation();
    const element = triggerRef.current || e.currentTarget;
    if (element) {
      const rect = element.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192 + window.scrollX,
      });
    }
    setOpen(!open);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  if (asChild && React.isValidElement(children)) {
    return (
      <span
        ref={triggerRef}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {children}
      </span>
    );
  }

  return (
    <button type="button" ref={triggerRef} onClick={handleClick} aria-expanded={open}>
      {children}
    </button>
  );
}

DropdownMenuTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  asChild: PropTypes.bool,
};

DropdownMenuTrigger.defaultProps = {
  asChild: false,
};

function DropdownMenuContent({ children, align, className }) {
  const { open, setOpen, position, menuRef } = useContext(DropdownMenuContext);

  useEffect(() => {
    if (open && menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Adjust if menu goes below viewport
      if (rect.bottom > viewportHeight) {
        menu.style.top = `${position.top - rect.height - 40}px`;
      }

      // Adjust if menu goes outside right edge
      if (rect.right > viewportWidth) {
        menu.style.left = `${viewportWidth - rect.width - 16}px`;
      }

      // Adjust if menu goes outside left edge
      if (rect.left < 0) {
        menu.style.left = '16px';
      }
    }
  }, [open, position, menuRef]);

  if (!open) return null;

  const menuContent = (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-[9999] min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        className,
      )}
      style={{
        top: position.top + 4,
        left: align === 'end' ? position.left : position.left,
      }}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { closeMenu: () => setOpen(false) });
        }
        return child;
      })}
    </div>
  );

  return ReactDOM.createPortal(menuContent, document.body);
}

DropdownMenuContent.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['start', 'end']),
  className: PropTypes.string,
};

DropdownMenuContent.defaultProps = {
  align: 'start',
  className: '',
};

function DropdownMenuItem({ children, onClick, disabled, className, closeMenu }) {
  const handleClick = (e) => {
    if (disabled) return;
    if (onClick) onClick(e);
    if (closeMenu) closeMenu();
  };

  return (
    <button
      type="button"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

DropdownMenuItem.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  closeMenu: PropTypes.func,
};

DropdownMenuItem.defaultProps = {
  onClick: undefined,
  disabled: false,
  className: '',
  closeMenu: undefined,
};

function DropdownMenuSeparator() {
  return <div className="-mx-1 my-1 h-px bg-muted" />;
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
};
